import { useLingui } from '@lingui/react/macro';
import { ArrowRight2 } from 'iconsax-reactjs';
import { type FC, useMemo, useState } from 'react';
import { Link } from '@/base/router';
import { useSettings } from '@/settings/api';
import type { BadgeProps } from '@/shared/Badge';
import { FeeTierBadge } from '@/shared/FeeTierBadge';
import { PopoverContent, PopoverRoot, PopoverTrigger } from '@/shared/Popover';
import { useMyTradeFees } from './api';
import { FeeRate } from './FeeRate';
import { rateSpread } from './rate';

type Spread = ReturnType<typeof rateSpread>;

const RateRow: FC<{ label: string; spread: Spread }> = ({ label, spread }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-xs text-content-tertiary">{label}</span>
    {spread ? (
      <div className="flex items-center gap-1">
        <FeeRate value={spread.min} className="text-sm" />
        {spread.varies && (
          <>
            <span className="text-content-tertiary text-xs">–</span>
            <FeeRate value={spread.max} className="text-sm" />
          </>
        )}
      </div>
    ) : (
      <span className="text-sm text-content-tertiary">---</span>
    )}
  </div>
);

/**
 * The user's own tier, with the rates it charges. The rates are derived from
 * the per-pair response rather than the public ladder, so they are right for a
 * private class too — one that never appears in `tiers/`.
 */
export const FeeTierPopover: FC<{
  size?: BadgeProps['size'];
  className?: string;
}> = ({ size, className }) => {
  const { t } = useLingui();
  const [{ language }] = useSettings();
  const [open, setOpen] = useState(false);
  const myFees = useMyTradeFees();

  const spreads = useMemo(
    () => ({
      maker: rateSpread((myFees.data?.fees ?? []).map((fee) => fee.maker_rate)),
      taker: rateSpread((myFees.data?.fees ?? []).map((fee) => fee.taker_rate)),
    }),
    [myFees.data],
  );

  const feeClass = myFees.data?.fee_class;
  if (!feeClass) return null;

  return (
    <PopoverRoot open={open} onOpenChange={setOpen}>
      {/* The badge sits inside a navigation link in the header menu, so the
          default is cancelled before the trigger stops the click bubbling. */}
      <span
        className="contents"
        onClickCapture={(event) => event.preventDefault()}
      >
        <PopoverTrigger openOnHover className={className}>
          <FeeTierBadge
            name={feeClass.name}
            size={size}
            className="cursor-help"
          />
        </PopoverTrigger>
      </span>
      <PopoverContent align="end" side="bottom" className="w-[248px] p-4">
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium">{feeClass.name}</span>
          {feeClass.description && (
            <p className="text-xs text-content-tertiary leading-relaxed">
              {feeClass.description}
            </p>
          )}
        </div>
        <div className="h-px bg-border-disabled my-3" />
        <div className="flex flex-col gap-2">
          <RateRow label={t`Maker`} spread={spreads.maker} />
          <RateRow label={t`Taker`} spread={spreads.taker} />
        </div>
        {(spreads.maker?.varies || spreads.taker?.varies) && (
          <p className="text-2xs text-content-tertiary mt-2 leading-relaxed">
            {t`Your rate differs by pair — see the full table.`}
          </p>
        )}
        <div className="h-px bg-border-disabled my-3" />
        <Link
          workspace="public"
          to={`/${language}/fee`}
          className="text-xs text-content-link hover:underline flex items-center justify-between gap-1"
          onClick={() => setOpen(false)}
        >
          {t`Compare all tiers`}
          <ArrowRight2 size={14} className="auto-rtl" />
        </Link>
      </PopoverContent>
    </PopoverRoot>
  );
};
